import React, { useMemo } from 'react';
import { PartialRecipe } from '../types/api/entities/recipe';
import { getRecipes } from '../store/actions/api/get-recipes';
import { getFilters } from '../store/actions/api/get-filters';
import { RecipeList } from '../components/recipe/list';
import { CONTENT_MAX_WIDTH, mediaQuery } from '../util/ui';
import { EntitiesState } from '../store/state/entities';
import { setSearch } from '../store/actions/ui/set-search';
import { getStore } from '../store/actions/api/get-store';
import { Frame } from '../components/frame/frame';
import { LinkCreator } from '../util/linker';
import { formatTitle } from '../util/format-title';
import Head from 'next/head';
import { BackLink } from '../components/back-link';
import { useTranslation } from 'next-i18next';
import { useAppDispatch, useAppSelector } from '../store/use-store';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { notUndefined } from '../util/not-undefined';

interface OwnProps {
  search?: string;
}

const classNames = {
  navigation: 'recipe-list__navigation',
};

type TrType = ReturnType<typeof useTranslation>['t'];

function getTitle({ search }: OwnProps, t: TrType): JSX.Element {
  if (search) {
    return <h1>{t('SearchResultsFor', { searchTerm: search })}</h1>;
  } else {
    return <h1>All recipes</h1>;
  }
}

function getTitleTerm({ search }: OwnProps): string {
  if (search) {
    return `"${search}"`;
  } else {
    return 'All';
  }
}

function getRecipesAndTotal(recipe: EntitiesState['recipe']): [PartialRecipe[], number] {
  const {
    query: { current },
    byId,
  } = recipe;

  return [current.ids.map((id) => byId[id]).filter(notUndefined), current.total];
}

export default function SearchPage() {
  const { t } = useTranslation();
  const { recipe, store } = useAppSelector((s) => {
    return s.entities;
  });
  const { ui } = useAppSelector((s) => s);

  const dispatch = useAppDispatch();

  const {
    query: { filter, query },
  } = useRouter();

  useEffect(() => {
    let search: string | undefined;

    // Parse query parameters
    if (typeof query === 'string') {
      search = query;
      if (!ui.search) {
        dispatch(setSearch(search));
      }
    }

    dispatch(getStore());
    dispatch(
      getRecipes({
        options: {
          search,
        },
      }),
    );
    dispatch(getFilters());
  }, [filter, query, dispatch, ui.search]);

  const [recipes, total] = getRecipesAndTotal(recipe);

  const recipeFilters = useMemo(() => [], []);

  return (
    <div>
      <Head>
        <title>
          {formatTitle(
            store,
            t('SearchTitle', {
              recipeType: getTitleTerm({ search: ui.search }),
            }),
          )}
        </title>
      </Head>

      <Frame>
        <div className="top">
          <div className="back-link">
            <BackLink text={t('AllRecipes')} href={LinkCreator.toRoot()} />
          </div>
          {getTitle({ search: ui.search }, t)}
        </div>

        <div className="content">
          <RecipeList
            hideFilters={true}
            deactivateRatings={
              store.theme && store.theme.deactivateRatings ? store.theme.deactivateRatings : false
            }
            recipes={recipes}
            filters={recipeFilters}
            onLoadMoreClick={() => {
              dispatch(
                getRecipes({
                  previousIds: recipes.map((r) => r.id),
                  loadMore: true,
                  options: {
                    search: ui.search,
                    offset: recipe.query.current.ids.length,
                  },
                }),
              );
            }}
            showMoreEnabled={total > recipes.length}
          />
        </div>
      </Frame>

      <style jsx>{`
        .top {
          display: flex;
          align-items: center;
          flex-direction: column;
        }

        div :global(h1),
        .back-link {
          width: 100%;
          max-width: ${CONTENT_MAX_WIDTH};
          text-align: center;
        }

        .back-link {
          margin-top: 20px;
        }

        div :global(h1) {
          font-family: var(--font-family-secondary);
          font-weight: normal;
          line-height: normal;
          color: #131415;
          margin-top: 16px;
          margin-bottom: 0;
          font-size: 30px;
        }

        .content {
          margin-top: 24px;
        }

        div :global(.${classNames.navigation}) {
          display: none;
        }

        @media ${mediaQuery.ExtraLarge} {
          div :global(h1),
          .back-link {
            text-align: left;
          }

          div :global(h1) {
            margin-top: 24px;
            font-size: 60px;
          }

          .content {
            margin-top: 37px;
          }

          div :global(.${classNames.navigation}) {
            display: block;
          }
        }
      `}</style>
    </div>
  );
}
export const getStaticProps: GetServerSideProps = async ({ locale }) => {
  locale = locale || '';

  return {
    props: { ...(await serverSideTranslations(locale, ['common'])) },
  };
};
